import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import InternalLayout from '../../components/InternalLayout';
import { internalApi } from '../../utils/api';

// PRD-008 F7 / AC-3 — the workspace list. Linear-style data table:
// 44px-ish rows, sortable column headers, search prominent at the top,
// pagination at the bottom. Row click navigates to the drill-down,
// which fires the reason-modal gate.
const SORT_OPTIONS = [
  { key: 'last_activity_at', label: 'Last activity' },
  { key: 'name', label: 'Name' },
  { key: 'created_at', label: 'Created' },
  { key: 'member_count', label: 'Members' },
  { key: 'agreement_count', label: 'Agreements' },
];

function relativeTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function OperatorWorkspaces() {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('last_activity_at');
  const [order, setOrder] = useState('desc');
  const [hasActive, setHasActive] = useState('any');
  const [workspaces, setWorkspaces] = useState([]);
  const [pagination, setPagination] = useState({ limit: 50, offset: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { sort, order, limit: pagination.limit, offset: pagination.offset };
      if (search) params.search = search;
      if (hasActive !== 'any') params.has_active_agreements = hasActive;
      const res = await internalApi.listWorkspaces(params);
      setWorkspaces(res.data.workspaces);
      setPagination(res.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [search, sort, order, hasActive, pagination.limit, pagination.offset]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const onSort = (key) => {
    if (sort === key) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setOrder('desc'); }
  };

  return (
    <InternalLayout pageTitle="Workspaces">
      <div className="bg-white rounded-md border border-gray-200 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
          <input
            type="text"
            placeholder="Search workspaces or member emails…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, offset: 0 })); }}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
          <select
            value={hasActive}
            onChange={(e) => setHasActive(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="any">Any agreement state</option>
            <option value="true">Has active agreements</option>
            <option value="false">No active agreements</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {SORT_OPTIONS.map((o) => (
                  <th key={o.key}
                      onClick={() => onSort(o.key)}
                      className="text-left px-4 py-2 font-medium text-gray-600 cursor-pointer select-none">
                    {o.label}{sort === o.key ? (order === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                ))}
                <th className="text-left px-4 py-2 font-medium text-gray-600">Connections</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Agents</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Tier</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-600">Loading…</td></tr>
              )}
              {!loading && workspaces.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-600">
                  No workspaces yet. The first one will appear here when someone signs up.
                </td></tr>
              )}
              {!loading && workspaces.map((w) => (
                <tr key={w.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <Link to={`/internal/workspaces/${w.id}`} className="text-slate-900 font-medium hover:underline">
                      {w.name}
                    </Link>
                    <div className="text-xs text-gray-600">{w.slug}</div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">{relativeTime(w.last_activity_at)}</td>
                  <td className="px-4 py-2.5 text-gray-700">{relativeTime(w.created_at)}</td>
                  <td className="px-4 py-2.5 text-gray-700">{w.member_count}</td>
                  <td className="px-4 py-2.5 text-gray-700">{w.agreement_count}</td>
                  <td className="px-4 py-2.5 text-gray-700 text-xs">
                    {w.connection_counts.active}a · {w.connection_counts.pending}p
                    {w.connection_counts.severed > 0 && ` · ${w.connection_counts.severed}s`}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">{w.agent_count}</td>
                  <td className="px-4 py-2.5 text-gray-600">{w.tier || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
          <span>{pagination.total.toLocaleString()} workspaces</span>
          <div className="flex gap-2">
            <button
              disabled={pagination.offset === 0}
              onClick={() => setPagination((p) => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <button
              disabled={pagination.offset + pagination.limit >= pagination.total}
              onClick={() => setPagination((p) => ({ ...p, offset: p.offset + p.limit }))}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        {error && <div className="px-4 py-2 text-sm text-red-700 bg-red-50 border-t border-red-200">{error}</div>}
      </div>
    </InternalLayout>
  );
}
